{/*
  * DspReportingPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/reporting
  * Documentation > MAX Demand Partners > DSPs > Report APIs
  */}
import { AlertMessage, AlertMessageType, DataTable, DocsPageModule, LabelFontFamily } from 'UI-Dash-Commons-UI';
import CodeSnippet from '../containers/CodeSnippetContainer';
import { IndentWidthType } from '../../../common';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

type DspReportingPageProps = {
    linkTargetTopPosition?: number;
}

const DspReportingPage: FunctionComponent<DspReportingPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Report APIs'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'DSPs see their performance on AppLovin RTB by means of the same performance metrics that AppLovin uses. '
                    + 'You access these metrics via the AppLovin DSP Report APIs.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'All AppLovin numbers are final for billing. '
                    + 'Please contact AppLovin immediately should you see discrepancies between numbers from your DSP and our API.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Base URL'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Send report requests via an HTTP '}/>
                <DocsPageModule.DocsPageContentCode children={'GET'}/>
                <DocsPageModule.DocsPageContentText text={' to the following base URL:'}/>
            </DocsPageModule.DocsPageContent>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'plaintext',
                    code: `https://r.applovin.com/report`
                } ]}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Example Final URL:'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'plaintext',
                    code: `https://r.applovin.com/report?api_key=[Insert_API_Key]&start=2017-05-01&end=2017-05-01&report_type=dsp_report&columns=platform,country,impressions,spend&sort_impressions=DESC&limit=100&format=csv`
                } ]}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Required Parameters'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>

            <DataTable
                data={[
                    {
                        attribute: 'API Key',
                        value: ' • \`api_key=\` Insert_API_Key {NEWLINE} {NEWLINE}' +
                            'You obtain this from {Account > General > Keys}(https://www.applovin.com/account#keys).'
                    },
                    {
                        attribute: 'Dates',
                        value: ' • \`start=\` YYYY-MM-DD {NEWLINE} {NEWLINE} • \`end=\` YYYY-MM-DD'
                    },
                    {
                        attribute: 'Columns',
                        value: ' • \`columns=\` API_Columns {NEWLINE} {NEWLINE} (See “Reporting Columns” below.)'
                    },
                    {
                        attribute: 'Misc',
                        value:
                            ' • \`report_type=\` Report_Type {NEWLINE} {NEWLINE} ' +
                            '       Valid values are: \`dsp_report\` , \`dsp_latency\` * {NEWLINE} {NEWLINE} ' +
                            ' • \`format=\` Format_Type {NEWLINE} {NEWLINE} ' +
                            '       Valid values are: \`csv\` , \`json\` {NEWLINE} {NEWLINE} ' +
                            '* As of March 1, 2024, the \`dsp_latency\` type is deprecated. Please reach out to your AppLovin representative if you have any questions.'
                    }
                ]}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: '',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: '',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                    ]
                }
                totalRowCount={4}
            />


            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Optional Parameters'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>

            <DataTable
                data={[
                    {
                        attribute: 'Filtering Columns',
                        value:
                            ' • To filter by a column, add the following parameter: \`filter_column_name=onefilter,anotherfilter\` {NEWLINE} {NEWLINE}' +
                            ' e.g. \`filter_country=us,gb\`'
                    },
                    {
                        attribute: 'Sorting Columns',
                        value:
                            ' • To sort by a column, add the following parameter: \`sort_column_name=ORDER\` {NEWLINE} {NEWLINE} ' +
                            'i.e. \`sort_requests=DESC\` or \`=ASC\`'
                    },
                ]}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: '',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'value',
                            Header: '',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                    ]
                }
                totalRowCount={2}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Reporting Columns'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DataTable
                data={[
                    {
                        column: '\`ad_type\`',
                        description: 'Ad type: \`BANNER\`, \`INTER\`, \`NATIVE\`'
                    },
                    {
                        column: '\`bundle\`',
                        description: ''
                    },
                    {
                        column: '\`campaign_domain\`',
                        description: 'Bid.adomain value in bid responses'
                    },
                    {
                        column: '\`country\`',
                        description: 'Country'
                    },
                    {
                        column: '\`day\`',
                        description: 'Date (MM/DD/YY)'
                    },
                    {
                        column: '\`device_type\`',
                        description: 'Device (Phone, Tablet, Other)'
                    },
                    {
                        column: '\`hour\`',
                        description: '00:00–23:00'
                    },
                    {
                        column: '\`impressions\`',
                        description: 'Impressions'
                    },
                    {
                        column: '\`platform\`',
                        description: 'Platform (iOS/Android/FireOS)'
                    },
                    {
                        column: '\`size\`',
                        description: 'Ad Size (BANNER, MREC, NATIVE, INTER, or LEADER)'
                    },
                    {
                        column: '\`spend\`',
                        description: 'Spend'
                    },
                ]}
                columns={
                    [
                        {
                            id: 'columns',
                            Header: 'Reporting Columns',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.column}/>
                        },
                        {
                            id: 'description',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                        },
                    ]
                }
                totalRowCount={11}
            />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Response Codes'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DataTable
                data={[
                    {
                        code: '100',
                        response_text: '\`HTTP_UNEXPECTED_RESPONSE\`',
                        description: 'Received an unexpected HTTP response code'
                    }, {
                        code: '200',
                        response_text: '\`HTTP_OKAY\`',
                        description: 'Successful bid response. See note below.'
                    }, {
                        code: '204',
                        response_text: '\`HTTP_NO_CONTENT\`',
                        description: 'No bid response'
                    }, {
                        code: '300',
                        response_text: '\`HTTP_UNEXPECTED_REDIRECT\`',
                        description: 'Temporary Redirect status response code indicates that the resource requested has been temporarily moved to the URL given by the \`Location\`  headers.'
                    }, {
                        code: '400',
                        response_text: '\`HTTP_BAD_REQUEST\`',
                        description: 'Request malformed'
                    }, {
                        code: '403',
                        response_text: '\`HTTP_FORBIDDEN\`',
                        description: 'Forbidden response status code indicates that the server understands the request but refuses to authorize it.'
                    }, {
                        code: '404',
                        response_text: '\`HTTP_NOT_FOUND\`',
                        description: 'Endpoint not found or invalid'
                    }, {
                        code: '408',
                        response_text: '\`HTTP_REQUEST_TIMEOUT\`',
                        description: 'Timeout above required response time (150ms)'
                    }, {
                        code: '500',
                        response_text: '\`HTTP_INTERNAL_SERVER_ERROR\`',
                        description: 'An error occurred on our service'
                    }, {
                        code: '502',
                        response_text: '\`HTTP_BAD_GATEWAY\`',
                        description: 'Bad Gateway server error response code indicates that the server, while acting as a gateway or proxy, received an invalid response from the upstream server.'
                    }, {
                        code: '503',
                        response_text: '\`HTTP_SERVICE_UNAVAILABLE\`',
                        description: 'Service Unavailable server error response code indicates that the server is not ready to handle the request.'
                    }, {
                        code: '2000',
                        response_text: '\`NETWORK_ALLOCATION_ERROR\`',
                        description: 'Networking error'
                    }, {
                        code: '2010',
                        response_text: '\`NETWORK_QPS_ERROR\`',
                        description: 'Networking error'
                    }, {
                        code: '2020',
                        response_text: '\`NETWORK_SOCKET_ERROR\`',
                        description: 'Networking error'
                    },
                ]}
                columns={
                    [
                        {
                            id: 'code',
                            Header: 'Code',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.code}/>
                        },
                        {
                            id: 'response_text',
                            Header: 'Response Text',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.response_text}/>
                        },
                        {
                            id: 'description',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                        },
                    ]
                }
                totalRowCount={14}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={`Note: `} />
                <DocsPageModule.DocsPageContentCode children={`200`} />
                <DocsPageModule.DocsPageContentText text={` (`} />
                <DocsPageModule.DocsPageContentCode children={`HTTP_OKAY`} />
                <DocsPageModule.DocsPageContentText text={`) responses include bids that were filtered for the below reasons. Use `} />
                <DocsPageModule.DocsPageContentAnchor
                    text={`the loss notification URL`}
                    to={`/documentation/mediation/auctions/dsps/ortb-spec/bid-response#win/loss/billing-notifications`}
                    newWindow={false}
                />
                <DocsPageModule.DocsPageContentText text={` with the loss reason macro (`} />
                <DocsPageModule.DocsPageContentCode children={`AUCTION_LOSS`} />
                <DocsPageModule.DocsPageContentText text={`) to get more granular details:`} />
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>invalid native markup</DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>invalid VAST</DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>bid with a blocked category</DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>bid with a blocked ad domain</DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>bid with a blocked package</DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>
        </>
    )
}

const DescriptionCellWrapper = styled( DocsPageModule.DocsPageContent )`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: normal;
  margin: 5px;
`;

export default DspReportingPage;
